import { POSTHOG_FEATURE_FLAGS, useFeatureFlagsReady, usePostHogFeatureFlag } from '../usePostHogFeatureFlag'
import { usePermissions } from '../usePermissions'

/**
 * Shared FGA gating for the `validation_static_links` domain.
 *
 * While `internal_fga_orgv_validation_static_links` is off the backend skips its
 * permission check, so every check here falls open to the pre-FGA behaviour — the same
 * window handling as `AccessRoute` and `resolveNavAccess`.
 *
 * The three writes all sit on `editor` in the model, so a caller holding one holds them
 * all today. They are kept as separate checks because the backend exposes them as three
 * permissions and the rungs may split later; reading them from one place means that
 * split needs no change here.
 *
 * Every check waits for `useFeatureFlagsReady()`. `usePostHogFeatureFlag` reads an
 * unresolved flag as `false`, which would fall open and render controls that then vanish
 * once the flag lands as on — the static links tab has no `AccessRoute` in front of it,
 * so that flicker would be visible.
 */
export function useStaticLinksAccess(): {
  canRead: boolean
  canCreate: boolean
  canUpdate: boolean
  canDelete: boolean
} {
  const { userHasPermissions } = usePermissions()
  const flagsReady = useFeatureFlagsReady()
  const internalFgaOrgvValidationStaticLinksFeatureFlag = usePostHogFeatureFlag(
    POSTHOG_FEATURE_FLAGS.INTERNAL_FGA_ORGV_VALIDATION_STATIC_LINKS
  )

  const isAllowed = (permission: string) =>
    flagsReady && (!internalFgaOrgvValidationStaticLinksFeatureFlag || userHasPermissions(permission))

  return {
    canRead: isAllowed('validation_static_links:can_read'),
    canCreate: isAllowed('validation_static_links:can_create'),
    canUpdate: isAllowed('validation_static_links:can_update'),
    canDelete: isAllowed('validation_static_links:can_delete')
  }
}
